import {
  INTRANET_IMAGE_SIDE,
  type IntranetEventKind,
  type IntranetLinkCategory,
  type IntranetPostKind,
  type IntranetPostStatus,
  type IntranetText,
} from '@loot/shared';

/** Publicação da intranet como a API devolve (ADR 0077). */
export interface IntranetPost {
  id: string;
  kind: IntranetPostKind;
  title: IntranetText;
  summary: Partial<IntranetText>;
  body: Partial<IntranetText>;
  ownerArea: string | null;
  urgent: boolean;
  displayFrom: string;
  displayUntil: string | null;
  startsAt: string | null;
  endsAt: string | null;
  allDay: boolean;
  location: string | null;
  eventKind: IntranetEventKind | null;
  version: string | null;
  effectiveFrom: string | null;
  documentUrl: string | null;
  status: IntranetPostStatus;
  publishedAt: string | null;
  updatedAt: string;
  /** Imagem atual da publicação (ou nenhuma) e a descrição para leitor de tela. */
  imageId: string | null;
  imageDescription: string | null;
}

/** Endereço da imagem de uma publicação (quem vê a publicação vê a imagem). */
export function imageUrl(imageId: string): string {
  return `/api/intranet/images/${imageId}`;
}

/**
 * Reduz a imagem no navegador antes de enviar (lado maior com INTRANET_IMAGE_SIDE pixels, JPEG),
 * para o arquivo ficar leve sem perder a leitura de um comunicado que é uma imagem.
 */
export async function resizeImage(file: File): Promise<Blob> {
  const image = await createImageBitmap(file);
  const scale = Math.min(1, INTRANET_IMAGE_SIDE / Math.max(image.width, image.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(image.width * scale);
  canvas.height = Math.round(image.height * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) return file;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  image.close();
  return new Promise((resolve) =>
    canvas.toBlob((blob) => resolve(blob ?? file), 'image/jpeg', 0.88),
  );
}

export interface IntranetLink {
  id: string;
  label: IntranetText;
  url: string;
  category: IntranetLinkCategory;
  sortOrder: number;
  isActive: boolean;
}

export interface IntranetHome {
  announcements: IntranetPost[];
  news: IntranetPost[];
  events: IntranetPost[];
  policies: IntranetPost[];
  links: IntranetLink[];
}

/** Data e hora (UTC) para o campo "datetime-local", no fuso de quem está na tela. */
export function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** O valor do campo "datetime-local" (fuso de quem está na tela) em UTC para gravar. */
export function fromLocalInput(local: string): string | null {
  return local ? new Date(local).toISOString() : null;
}

/** Endereço de tela do próprio sistema abre na mesma aba; o resto, em outra. */
export function isInternal(url: string): boolean {
  return url.startsWith('/');
}

/**
 * Dia e mês da caixa de data da agenda, pelo tipo de cada parte do formatador (a ordem muda com o
 * idioma: "4 de nov." em português, "Nov 4" em inglês). O ponto da abreviação sai do mês.
 */
export function dateBox(
  date: Date,
  lang: string,
  timeZone?: string,
): { day: string; month: string } {
  const parts = new Intl.DateTimeFormat(lang, {
    day: 'numeric',
    month: 'short',
    timeZone,
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  return { day: part('day'), month: part('month').replace(/\.$/, '') };
}
